import {
  faCaretDown, faCaretUp,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useCallback, useRef,
} from 'react';
import type { Scrollbar } from 'react-scrollbars-custom';
import { DividerComponent } from '~/_shared/baseComponents/divider';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { NumberInCircleComponent } from '~/_shared/baseComponents/numberInShape/numberInCircle.component';
import {
  TooltipComponent, TooltipPlacement,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import {
  ScrollBarComponent, ScrollbarType,
} from '~/_shared/components/scrollbar/scrollbar.component';
import { useScrollControls } from '~/_shared/components/scrollbar/useScrollControls.hook';
import { isTouchOnly } from '~/_shared/utils/deviceDetect/deviceDetect.helpers';
import {
  arrowStyle, arrowsWrapperStyle, contentStyle, dividerStyle, dividerWrapperStyle, fadeBottomStyle, fadeTopStyle,
  getBrighterColor, getFontColor, ITEM_SIZE, itemStyle, overlappingBackgroundStyle,
} from '~/map/mapToolControls/menuItems/overlappingUI/overlappingUI.styles';
import { useOverlappingElementsController } from '~/map/mapToolControls/menuItems/overlappingUI/useOverlappingElementsController.hook';

const isTouchOnlyDevice = isTouchOnly();

export const OverlappingUIPanel: FC = _ => {
  const scrollbarRef = useRef<Scrollbar | null>(null);
  const {
    atBottom, atTop, scrollDown, scrollUp, stopScrolling, updateScrollBoundaries, scrollYPossible,
  } = useScrollControls(scrollbarRef);
  const { overlappingElements, getClickElementHandler } = useOverlappingElementsController();

  const handleScrollUp = useCallback(() => {
    if (!atTop) {
      scrollUp();
    }
  }, [atTop, scrollUp]);

  const handleScrollDown = useCallback(() => {
    if (!atBottom) {
      scrollDown();
    }
  }, [atBottom, scrollDown]);

  if (overlappingElements.length < 2) {
    return null;
  }

  return (
    <div css={overlappingBackgroundStyle}>

      {scrollYPossible && !isTouchOnlyDevice && (
        <>
          <div css={arrowsWrapperStyle}>
            <div
              css={arrowStyle(atTop)}
              onMouseDown={handleScrollUp}
              onMouseUp={stopScrolling}
              onMouseLeave={stopScrolling}
            >
              <FontAwesomeIcon icon={faCaretUp} />
            </div>
            <div
              css={arrowStyle(atBottom)}
              onMouseDown={handleScrollDown}
              onMouseUp={stopScrolling}
              onMouseLeave={stopScrolling}
            >
              <FontAwesomeIcon icon={faCaretDown} />
            </div>
          </div>

          <div css={dividerWrapperStyle}>
            <DividerComponent css={dividerStyle} />
            <div css={!isTouchOnlyDevice && fadeTopStyle(!atTop)} />
          </div>
        </>
      )}

      <div css={fadeTopStyle(isTouchOnlyDevice && !atTop)} />
      <ScrollBarComponent
        contentStyle={contentStyle}
        getScrollbarReference={(ref) => scrollbarRef.current = ref}
        onUpdate={updateScrollBoundaries}
        translateContentHeightToHolder
        type={ScrollbarType.Vertical}
      >
        {overlappingElements.map((item, index) => (
          <TooltipComponent
            key={`${index}|${item.name}`}
            placement={TooltipPlacement.Right}
            tooltipContent={item.name}
          >
            <span onClick={getClickElementHandler(index)} >
              <NumberInCircleComponent
                colors={{
                  fontColor: getFontColor(item.color),
                  fillColor: item.active ? getBrighterColor(item.color).hex : item.color,
                }}
                css={itemStyle(item.color, item.active, item.label.toString().length)}
                size={ITEM_SIZE}
              >
                {item.label}
              </NumberInCircleComponent>
            </span>
          </TooltipComponent>
        ))}
      </ScrollBarComponent>
      <div css={fadeBottomStyle(!atBottom)} />
    </div>
  );
};
